import {
  Fragment,
  useEffect,
  useRef,
  type DependencyList,
  type ReactElement,
  type ReactNode,
  type RefObject,
} from 'react';
import { Link, NavLink, useLocation } from 'react-router';
import { Icon, type IconName } from '../ui/Icon';
import s from './LevelTwoTabs.module.css';

export interface LevelTwoItem {
  id: string;
  label: string;
  href: string;
  /** Match the route exactly (for an index tab). */
  end?: boolean;
  /** Optional count shown after the label. */
  count?: number;
  /** What the count is, read out after it ("new": "My feedback, 1 new"). */
  countName?: string;
  /**
   * APP-236 step 2: `waiting` draws the count as Manage's gold chip, hidden from screen readers,
   * and names the link "Approvals, 3 waiting" (APP-144's label-in-name rule). Never 0.
   */
  countTone?: 'waiting';
  /** Optional decorative icon before the label (hidden from screen readers; the label names it). */
  icon?: IconName;
  /**
   * Other pages that belong to this tab (opened from it, with no tab of their own): the tab stays
   * marked as current on them, so a person always sees where they are.
   */
  also?: readonly string[];
}

/** One group in the row above the tabs (APP-236: Manage's pills, one per role). */
export interface LevelTwoGroup {
  id: string;
  label: string;
  /** Where the group opens: its first page. */
  href: string;
  /** Optional count shown after the label (what waits in the group), never 0. */
  count?: number;
  /** What the count is, read out after it ("waiting": "Executive, 3 waiting"). */
  countName?: string;
  /** As `LevelTwoItem.countTone`. */
  countTone?: 'waiting';
}

/** The row of groups above the tabs, and which is current (APP-236, option C). */
export interface LevelTwoGroups {
  /** Names the groups' navigation ("Manage groups"). */
  label: string;
  items: readonly LevelTwoGroup[];
  /** The group whose pages the tabs are: filled, with `aria-current="true"`. */
  current: string | null;
  /** A thin divider after this group (the person's main work), hidden from screen readers. */
  dividerAfter?: string;
}

/** Room under the bar for a focus ring (2 px, 2 px out from its control) and the bar's shadow. */
const RING_ROOM = 8;

export interface LevelTwoTabsProps {
  /** Names the navigation ("Insights sections", "Committee pages"). */
  label: string;
  /**
   * Items the person can use. Fewer than two → no Level 2 bar at all, unless there are none and
   * there are `actions`: then the bar holds just those (an Executive's decisions by kind, for a
   * Stock Admin who can't open the Executive's pages). Under a row of groups, a group's one page
   * still shows, so the tabs always say which page this is.
   */
  items: readonly LevelTwoItem[];
  /** Controls on the right of the bar (period picker, Export). */
  actions?: ReactNode;
  /**
   * The current item's id, for tabs that differ only by their query (`?tab=mine`), which route
   * matching can't tell apart. Without it the route decides; `null` marks none as current.
   */
  current?: string | null;
  /**
   * APP-236: a first row of groups above the tabs (Manage's, by role), when there are two or more;
   * with one there's no first row, and the tabs follow the usual rule.
   */
  groups?: LevelTwoGroups;
}

/**
 * QA-052 (APP-087 §5): on a narrow screen a row keeps its whole labels and scrolls sideways inside
 * itself. The current link is brought into view, and a fade at either end says more are that way.
 * Written straight to the element, so scrolling never re-renders the bar.
 */
function useSideScroll(
  wrapRef: RefObject<HTMLDivElement | null>,
  navRef: RefObject<HTMLElement | null>,
  currentSelector: string,
  deps: DependencyList,
): void {
  useEffect(() => {
    const wrap = wrapRef.current;
    const nav = navRef.current;
    if (wrap === null || nav === null) return;
    const now = nav.querySelector<HTMLElement>(currentSelector);
    if (now !== null) {
      const box = nav.getBoundingClientRect();
      const link = now.getBoundingClientRect();
      if (link.left < box.left) nav.scrollLeft += link.left - box.left - 16;
      else if (link.right > box.right) nav.scrollLeft += link.right - box.right + 16;
    }
    const edges = () => {
      wrap.dataset['before'] = String(nav.scrollLeft > 1);
      wrap.dataset['after'] = String(nav.scrollLeft + nav.clientWidth < nav.scrollWidth - 1);
    };
    edges();
    nav.addEventListener('scroll', edges, { passive: true });
    window.addEventListener('resize', edges);
    return () => {
      nav.removeEventListener('scroll', edges);
      window.removeEventListener('resize', edges);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the caller names what moves the row.
  }, deps);
}

/**
 * A link's label and its count, and the link's name when the count needs one. Neutral: the number
 * for the eye, its words for screen readers ("My feedback, 1 new"). Waiting (APP-236 step 2):
 * Manage's gold chip, hidden from screen readers, with the link named "Approvals, 3 waiting" as the
 * bell is named, so the visible label starts the name (APP-144). A visually hidden span can't do
 * it here: it's positioned, so Chromium reads "Approvals , 3 waiting". The space between label and
 * chip keeps axe's label-in-name check reading "Approvals 3", not "Approvals3". Nothing for 0.
 */
function labelled(
  label: string,
  count: number | undefined,
  name: string | undefined,
  tone: 'waiting' | undefined,
): { children: ReactElement; ariaLabel?: string } {
  if (tone === 'waiting')
    return count === undefined || count <= 0
      ? { children: <>{label}</> }
      : {
          ariaLabel: `${label}, ${String(count)} ${name ?? 'waiting'}`,
          children: (
            <>
              {label}{' '}
              <span className={s.waitingChip} aria-hidden="true">
                {count}
              </span>
            </>
          ),
        };
  return {
    children: (
      <>
        {label}
        {count !== undefined && (
          <span className={s.count}>
            <span className="visually-hidden">, </span>
            {count}
            {name !== undefined && <span className="visually-hidden"> {name}</span>}
          </span>
        )}
      </>
    ),
  };
}

/**
 * Level 2 navigation under the hero. Each tab is a route link, so the current one is marked with
 * `aria-current="page"` (navigation links rather than an in-page tablist: they change the URL).
 * An explicit `current` decides first; otherwise a tab is current on its own route or on one of
 * the pages it lists in `also`. With `groups` (APP-236), a row of groups sits above the tabs in the
 * same sticky bar, so the bar's measured height covers both rows.
 */
export function LevelTwoTabs({
  label,
  items,
  actions,
  current,
  groups,
}: LevelTwoTabsProps): ReactElement | null {
  const { pathname, search } = useLocation();
  const barRef = useRef<HTMLDivElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLElement>(null);
  const groupWrapRef = useRef<HTMLDivElement>(null);
  const groupNavRef = useRef<HTMLElement>(null);
  const groupRow = groups !== undefined && groups.items.length >= 2;
  const tabs = items.length >= 2 || (groupRow && items.length === 1);
  const shown = tabs || groupRow || (items.length === 0 && actions !== undefined);
  // WCAG 2.4.11: the bar sticks to the top of the window, so it says how much of the window it
  // covers, and the page's scroll padding takes that (global.css). Measured, because the bar is
  // taller when its actions wrap under the tabs, when it has a row of groups (APP-236), or when the
  // text is larger.
  useEffect(() => {
    const bar = barRef.current;
    if (bar === null) return;
    const root = document.documentElement;
    const measure = () => {
      root.style.setProperty('--covered-top', `${String(bar.offsetHeight + RING_ROOM)}px`);
    };
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(bar);
    return () => {
      observer?.disconnect();
      root.style.removeProperty('--covered-top');
    };
  }, [shown]);
  useSideScroll(wrapRef, navRef, '[aria-current="page"]', [
    pathname,
    search,
    current,
    items.length,
  ]);
  useSideScroll(groupWrapRef, groupNavRef, '[aria-current="true"]', [
    pathname,
    groups?.current,
    groups?.items.length,
  ]);
  if (!shown) return null;
  const tab = (item: LevelTwoItem): ReactElement => {
    const { children, ariaLabel } = labelled(
      item.label,
      item.count,
      item.countName,
      item.countTone,
    );
    const named = ariaLabel === undefined ? {} : { 'aria-label': ariaLabel };
    const content = (
      <>
        {item.icon !== undefined && <Icon name={item.icon} size={14} />}
        {children}
      </>
    );
    if (current !== undefined)
      return (
        <Link
          key={item.id}
          to={item.href}
          className={s.tab}
          {...named}
          {...(item.id === current && { 'aria-current': 'page' as const })}
        >
          {content}
        </Link>
      );
    const belongs = (item.also ?? []).some(
      (path) => pathname === path || pathname.startsWith(`${path}/`),
    );
    return belongs ? (
      <Link key={item.id} to={item.href} aria-current="page" className={s.tab} {...named}>
        {content}
      </Link>
    ) : (
      <NavLink key={item.id} to={item.href} end={item.end ?? false} className={s.tab} {...named}>
        {content}
      </NavLink>
    );
  };
  const last = groups?.items[groups.items.length - 1]?.id;
  return (
    <div ref={barRef} className={s.bar} {...(groupRow && { 'data-groups': 'true' })}>
      {groupRow && (
        <div className={s.groupsRow}>
          <div ref={groupWrapRef} className={s.groupsWrap}>
            <nav ref={groupNavRef} aria-label={groups.label} className={s.groupsList}>
              {groups.items.map((group) => {
                // A pill never shows a 0, in either tone (step 1's rule).
                const pill = labelled(
                  group.label,
                  group.count !== undefined && group.count > 0 ? group.count : undefined,
                  group.countName,
                  group.countTone,
                );
                return (
                  <Fragment key={group.id}>
                    <Link
                      to={group.href}
                      className={s.pill}
                      {...(pill.ariaLabel !== undefined && { 'aria-label': pill.ariaLabel })}
                      {...(group.id === groups.current && { 'aria-current': 'true' as const })}
                    >
                      {pill.children}
                    </Link>
                    {group.id === groups.dividerAfter && group.id !== last && (
                      <span aria-hidden="true" className={s.divider} />
                    )}
                  </Fragment>
                );
              })}
            </nav>
          </div>
        </div>
      )}
      <div className={s.inner}>
        {tabs && (
          <div ref={wrapRef} className={s.listWrap}>
            <nav ref={navRef} aria-label={label} className={s.list}>
              {items.map(tab)}
            </nav>
          </div>
        )}
        {actions !== undefined && <div className={s.actions}>{actions}</div>}
      </div>
    </div>
  );
}
